/**
 * Campus Ambassador landing page (route: /cap/:slug).
 *
 * Behaviour:
 *  • Active CA slug → stamp `oodel_cap_slug` in localStorage and route the
 *    visitor through the normal signup flow (/register). The signup endpoint
 *    picks the slug up from localStorage and creates the conversion record.
 *  • Paused / terminated CA → backend returns `redirect_to_invite_code`;
 *    we hop to /invite/<peer-code> so the visitor still gets to the right
 *    inviter — the link never 404s after demotion.
 *  • Unknown slug → soft fail to homepage (matches /invite/{code} behaviour).
 *
 * All on-page copy (overline, headline template, intro, the 3 pillars, CTA,
 * footer labels) is sourced from `site_content` via `data.copy` so admins
 * can edit it from the dashboard without a code deploy. {name} / {campus}
 * placeholders are substituted server-side. We keep `FALLBACK_COPY` as a
 * safety net so the page never renders blank if the API hiccups.
 */
import { useEffect, useState } from "react";
import { useParams, Navigate, useNavigate } from "react-router-dom";
import api from "../lib/api";
import Logo from "../components/Logo";

const FALLBACK_COPY = {
  cap_overline: "CAMPUS AMBASSADOR INVITE",
  cap_headline: "Your campus ambassador brought you to oodel.",
  cap_intro: "Your campus ambassador is inviting you to join Pakistan's first nationally representative consumer-intelligence panel. Take short surveys, earn points, get paid.",
  cap_pillar_1_title: "Earn points",
  cap_pillar_1_body: "Every survey you complete pays. Cash out for Foodpanda, Daraz, EasyPaisa.",
  cap_pillar_2_title: "Stay anonymous",
  cap_pillar_2_body: "Your answers are aggregated. Your identity stays hidden.",
  cap_pillar_3_title: "Shape the data",
  cap_pillar_3_body: "Help build the most representative panel for Pakistan.",
  cap_cta_label: "Create your account →",
  cap_already_member_text: "Already a member?",
  cap_signin_link_text: "Sign in instead",
  cap_link_label: "Invite link:",
};

export default function CampusAmbassadorLanding() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [state, setState] = useState({ loading: true, data: null });

  useEffect(() => {
    if (!slug) { nav("/", { replace: true }); return; }
    // Stamp immediately so the signup form can read it even if the API call
    // is still in flight.
    try { localStorage.setItem("oodel_cap_slug", slug); } catch { /* no-op */ }
    api.get(`/cap/${slug}`)
      .then((r) => setState({ loading: false, data: r.data }))
      .catch(() => setState({ loading: false, data: { active: false } }));
  }, [slug, nav]);

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="text-neutral-400">Loading your invite…</div>
      </div>
    );
  }

  const d = state.data || {};
  // Smart redirect — demoted CA bounces to their original peer code link so
  // the URL never looks broken.
  if (!d.active && d.redirect_to_invite_code) {
    // Clear the cap_slug stamp so we don't keep trying to attribute to a
    // terminated CA on the subsequent signup.
    try { localStorage.removeItem("oodel_cap_slug"); } catch { /* no-op */ }
    return <Navigate to={`/invite/${d.redirect_to_invite_code}`} replace />;
  }
  if (!d.active && !d.redirect_to_invite_code) {
    // Unknown / hard-terminated slug → soft fail to homepage.
    try { localStorage.removeItem("oodel_cap_slug"); } catch { /* no-op */ }
    return <Navigate to="/" replace />;
  }

  const copy = { ...FALLBACK_COPY, ...(d.copy || {}) };

  return (
    <div className="min-h-screen bg-gradient-to-b from-neutral-50 to-white flex flex-col" data-testid="cap-landing">
      <div className="max-w-3xl mx-auto p-6 md:p-12 w-full flex-1">
        <Logo size="md" />
        <div className="mt-12">
          <div className="overline text-mango font-bold text-sm" data-testid="cap-overline">{copy.cap_overline}</div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter mt-2 leading-none" data-testid="cap-headline">
            {copy.cap_headline}
          </h1>
          <p className="text-base text-neutral-700 mt-4 max-w-prose" data-testid="cap-intro">
            {copy.cap_intro}
          </p>

          <div className="grid sm:grid-cols-3 gap-3 mt-8">
            <Pillar title={copy.cap_pillar_1_title} body={copy.cap_pillar_1_body} />
            <Pillar title={copy.cap_pillar_2_title} body={copy.cap_pillar_2_body} />
            <Pillar title={copy.cap_pillar_3_title} body={copy.cap_pillar_3_body} />
          </div>

          <div className="mt-10">
            <button
              onClick={() => nav(`/register`)}
              data-testid="cap-cta"
              className="inline-flex items-center gap-2 bg-black text-mango px-6 py-3 border-2 border-black font-bold uppercase tracking-wider hard-shadow hover:translate-x-[2px] hover:translate-y-[2px] transition-transform"
            >
              {copy.cap_cta_label}
            </button>
            <div className="text-xs text-neutral-500 mt-3">
              {copy.cap_already_member_text} <a href="/login" className="underline" data-testid="cap-signin-link">{copy.cap_signin_link_text}</a>
            </div>
          </div>

          <div className="mt-12 text-[11px] text-neutral-500 border-t border-neutral-200 pt-4">
            {copy.cap_link_label} <span className="font-mono">/cap/{slug}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function Pillar({ title, body }) {
  return (
    <div className="border-2 border-black bg-white p-4 hard-shadow">
      <div className="font-bold mb-1">{title}</div>
      <div className="text-xs text-neutral-600">{body}</div>
    </div>
  );
}
